import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Price from 'components/shared/Price'
import Button from 'components/shared/Button'
import { useIntl } from 'react-intl'
import { EditPositionCommand } from 'store/api-slice/hooks'
import { PromiseResponse } from 'helpers/types'
import { setIsErrorModalOpened } from 'store/general-slice'
import { useAppDispatch } from 'store/hooks'

interface EditorSummaryProps {
    handleCancel: () => void
    currentPrice: number
    newPrice: number | null
    updatedTicket?: EditPositionCommand
    isSavingDisabled?: boolean
    editItem: (
        editPositionCommand: EditPositionCommand
    ) => Promise<PromiseResponse>
    isFetching?: boolean
    index: number
    testIdPrefix?: string
}

const EditorSummary = ({
    handleCancel,
    currentPrice,
    newPrice,
    updatedTicket,
    isSavingDisabled,
    editItem,
    isFetching,
    index,
    testIdPrefix,
}: EditorSummaryProps) => {
    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()

    const handleSave = () => {
        if (!updatedTicket) return

        editItem(updatedTicket).then(({ isSuccess }) => {
            if (isSuccess) handleCancel()
            else dispatch(setIsErrorModalOpened(true))
        })
    }

    return (
        <Box
            isFullWidth
            direction={['column', 'row']}
            align={['flex-end', 'center']}
            justify={['center', 'space-between']}
            gap={12}
        >
            <Box
                direction={['column', 'row']}
                align={['flex-end', 'flex-start']}
                gap={[0, 24]}
            >
                <Box
                    direction={['row', 'column']}
                    align={['center', 'flex-start']}
                    justify={['flex-end', 'flex-start']}
                >
                    <Text variant="bodySmall" mr={4} color="grayscale_700">
                        {formatMessage({
                            id: 'selfcare.editor.current_price',
                            defaultMessage: 'Obecna cena:',
                        })}
                    </Text>
                    <Price
                        value={currentPrice}
                        size="heading4"
                        color="grayscale_700"
                    />
                </Box>
                <Box
                    direction={['row', 'column']}
                    align={['center', 'flex-start']}
                    justify={['flex-end', 'flex-start']}
                >
                    <Text variant="bodySmall" mr={4} color="secondary_500">
                        {formatMessage({
                            id: 'selfcare.editor.new_price',
                            defaultMessage: 'Nowa cena:',
                        })}
                    </Text>

                    <Price
                        size="heading4"
                        value={!isSavingDisabled ? newPrice : null}
                    />
                </Box>
            </Box>

            <Box direction="row" align="center" gap={24}>
                <Text
                    as="button"
                    variant="bodyStrong"
                    color="grayscale_700"
                    onClick={() => handleCancel()}
                    data-testid={`${
                        testIdPrefix ?? 'selfcare'
                    }-${index}-decline-changes`}
                >
                    {formatMessage({
                        id: 'global.btn.cancel',
                        defaultMessage: 'Anuluj',
                    })}
                </Text>
                <Button
                    variant="secondary"
                    onClick={handleSave}
                    isLoading={isFetching}
                    isDisabled={isSavingDisabled}
                    width="initial"
                    testId={`${
                        testIdPrefix ?? 'selfcare'
                    }-${index}-save-changes`}
                >
                    {formatMessage({
                        id: 'global.btn.save_changes',
                        defaultMessage: 'Zapisz zmiany',
                    })}
                </Button>
            </Box>
        </Box>
    )
}

export default EditorSummary
